/**
 * LifecycleEventsPanel — per-VPS chronological event feed (OCP-104).
 *
 * Renders ocp_audit_log entries where target_type='vps' AND
 * target_id=<serverId>. Surfaced from `GET /v1/admin/vps-servers/:id/
 * lifecycle-events`. Newest-first, with an action-filter dropdown,
 * a "load more" pagination button, and click-to-expand metadata JSON
 * rows for inspection.
 *
 * Mounted as the 5th section of AdminServerDetailPage. The endpoint
 * is server-scoped + 404-aware, so this panel never shows another
 * server's events — even if the operator hand-edited the URL.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface LifecycleEvent {
  id: string;
  action: string;
  operator_email: string;
  target_type: string;
  target_id: string | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

interface ListResponse {
  items: LifecycleEvent[];
  has_more: boolean;
}

const PAGE_SIZE = 50;

// Known vps_server.* event-type prefix list — populates the filter
// dropdown. Kept short on purpose; the operator can clear the filter
// to see everything. Adds new entries automatically by scanning rows
// the API actually returns.
const KNOWN_ACTIONS = [
  'vps_server.register',
  'vps_server.registered_manual',
  'vps_server.register.dry_run',
  'vps_server.register.failed',
  'vps_server.update',
  'vps_server.decommission',
  // OCP-552: the `vps_server.provision*` actions the Hetzner provisioner
  // emitted are gone with it. Historical rows still render (they just fall
  // to the default badge tone); nothing can produce a new one, so offering
  // them as filter options would be dead UI.
];

export function LifecycleEventsPanel({ serverId }: { serverId: string }) {
  const [items, setItems] = useState<LifecycleEvent[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const load = useCallback(
    async (mode: 'replace' | 'append') => {
      const offset = mode === 'append' ? items.length : 0;
      const setLoadingFn = mode === 'append' ? setLoadMoreLoading : setLoading;
      setLoadingFn(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('limit', String(PAGE_SIZE));
        params.set('offset', String(offset));
        if (actionFilter) params.set('action', actionFilter);
        const r = await api<ListResponse>(
          `/v1/admin/vps-servers/${serverId}/lifecycle-events?${params}`,
        );
        if (mode === 'append') {
          setItems((prev) => [...prev, ...r.items]);
        } else {
          setItems(r.items);
          setExpandedIds(new Set());
        }
        setHasMore(r.has_more);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setError('This server does not exist.');
        } else if (err instanceof ApiError) {
          setError(`Could not load events (HTTP ${err.status}).`);
        } else {
          setError('Could not load events.');
        }
      } finally {
        setLoadingFn(false);
      }
    },
    [serverId, actionFilter, items.length],
  );

  // Reload on filter change. Excluding `load` from deps because it
  // depends on items.length and re-creating the closure on every
  // append would re-trigger the effect mid-pagination.
  useEffect(() => {
    void load('replace');
  }, [serverId, actionFilter]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Merge known actions with any unknown actions present in the
  // loaded rows — keeps the dropdown self-discovering when the
  // backend adds a new audit-action name without a frontend deploy.
  const actionOptions = Array.from(
    new Set([...KNOWN_ACTIONS, ...items.map((i) => i.action)]),
  ).sort();

  return (
    <section data-testid="lifecycle-events-panel">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          Lifecycle events ({items.length}
          {hasMore ? '+' : ''})
        </h2>
        <div className="flex items-center gap-2 text-xs">
          <label htmlFor="lifecycle-action-filter" className="text-gray-500">
            Filter:
          </label>
          <select
            id="lifecycle-action-filter"
            data-testid="lifecycle-action-filter"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs"
          >
            <option value="">All actions</option>
            {actionOptions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {loading && items.length === 0 && (
        <p className="text-sm text-gray-500">Loading events...</p>
      )}

      {!loading && items.length === 0 && !error && (
        <p className="text-sm text-gray-500">
          No events recorded yet for this server.
        </p>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2">Timestamp</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Actor</th>
                <th className="px-3 py-2 w-12 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {items.map((evt) => {
                const expanded = expandedIds.has(evt.id);
                const hasMeta = evt.metadata != null && Object.keys(evt.metadata).length > 0;
                return (
                  <Row
                    key={evt.id}
                    evt={evt}
                    expanded={expanded}
                    hasMeta={hasMeta}
                    onToggle={() => toggleExpanded(evt.id)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            data-testid="lifecycle-load-more"
            onClick={() => void load('append')}
            disabled={loadMoreLoading}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
          >
            {loadMoreLoading ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

function Row({
  evt,
  expanded,
  hasMeta,
  onToggle,
}: {
  evt: LifecycleEvent;
  expanded: boolean;
  hasMeta: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        data-testid={`lifecycle-row-${evt.id}`}
        className={hasMeta ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60' : ''}
        onClick={hasMeta ? onToggle : undefined}
      >
        <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
          {formatTimestamp(evt.created_at)}
        </td>
        <td className="px-3 py-2 font-mono text-xs">
          <ActionBadge action={evt.action} />
        </td>
        <td className="px-3 py-2 text-xs">
          {evt.operator_email === 'system' ? (
            <span className="rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 text-gray-700 dark:text-gray-300">
              system
            </span>
          ) : (
            evt.operator_email
          )}
        </td>
        <td className="px-3 py-2 text-right text-xs text-gray-400">
          {hasMeta ? (expanded ? '−' : '+') : ''}
        </td>
      </tr>
      {expanded && hasMeta && (
        <tr data-testid={`lifecycle-row-meta-${evt.id}`}>
          <td colSpan={4} className="bg-gray-50 dark:bg-gray-800 px-3 py-2">
            <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-gray-700 dark:text-gray-300">
              {JSON.stringify(evt.metadata, null, 2)}
            </pre>
            {evt.ip_address && (
              <p className="mt-1 text-[10px] text-gray-500">
                IP: {evt.ip_address}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function ActionBadge({ action }: { action: string }) {
  const tone = action.includes('.failed')
    ? 'bg-red-100 text-red-800 dark:text-red-300'
    : action.includes('.dry_run')
      ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
      : action.endsWith('.decommission')
        ? 'bg-amber-100 text-amber-900 dark:text-amber-200'
        : action.endsWith('.register') || action.endsWith('.registered_manual')
          ? 'bg-emerald-100 text-emerald-800 dark:text-emerald-300'
          : 'bg-sky-100 text-sky-800';
  return <span className={`rounded px-1.5 py-0.5 ${tone}`}>{action}</span>;
}

function formatTimestamp(iso: string): string {
  try {
    return fmtDateTime(iso);
  } catch {
    return iso;
  }
}
